import React, { useEffect, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import { CreditsSummary, HolderAccount } from '@odiupsk/up-api-client'
import { _ } from '../../common/utils/intl'
import {
  Bar,
  BarItem,
  Button,
  ButtonLayout,
  DialogContent,
  DialogFooter,
  DialogHeader,
  ReachDialog,
  ReachTooltip,
  Loader,
} from '../../common/ui-kit'
import { formatMoney, formatDate } from '../../common/utils/format'
import { holderAccountsApi } from '../../common/api'
import { logError } from '../../common/api/utils'

export const CreditExpirationModal: React.FC<{
  holderAccount: HolderAccount
  close: () => void
}> = observer(({ holderAccount, close }) => {
  const [cardCreditsSummary, setCreditSummary] = useState<CreditsSummary>()
  const [loading, setLoading] = useState(false)

  const showAquaCreditsSummary = cardCreditsSummary?.credit_aqua_charge_annual_summary! >= 0
  const showBenefit500CreditsSummary =
    cardCreditsSummary?.credit_benefit500_charge_annual_summary! >= 0

  const haveGradualDissolutionOfCredit = () => cardCreditsSummary?.real_credit

  useEffect(() => {
    const fetchData = async () => {
      if (!holderAccount?.id) return
      setLoading(true)
      try {
        const { data } =
          (await holderAccountsApi.getHolderAccountCreditsSummary(holderAccount?.id)) || {}

        setCreditSummary(data!)
        setLoading(false)
      } catch (err) {
        logError(err) // Show error toast
        setLoading(false) // Stop loading
        close() // Close modal
        setCreditSummary({}) // Set credit summary to empty object to prevent errors
      }
    }

    fetchData()
  }, [])

  const modalContent = () => {
    if (loading) {
      return (
        <DialogContent className="mb-large">
          <Bar align="middle">
            <BarItem
              isFilling
              style={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: '110px',
                paddingBottom: '110px',
              }}>
              <Loader size="xxlarge" />
            </BarItem>
          </Bar>
        </DialogContent>
      )
    }
    return (
      <DialogContent className="mb-large">
        <div className="text-color-grey-600" data-testid="current-balance">
          {haveGradualDissolutionOfCredit() ? (
            <>
              <Trans>Disponibilný zostatok</Trans>:
            </>
          ) : (
            <>
              <Trans>Aktuálny zostatok</Trans>:
            </>
          )}

          {(showAquaCreditsSummary || showBenefit500CreditsSummary) && (
            <ReachTooltip
              ariaLabel=""
              label={<Trans>Súhrnný zostatok za všetky typy kreditu</Trans>}>
              <span
                style={{
                  display: 'inline-flex',
                  position: 'relative',
                  top: -5,
                  marginLeft: 2,
                }}>
                <img width="15" height="15" src="/static/img/info.png" alt="info" />
              </span>
            </ReachTooltip>
          )}
        </div>
        <div className="h2 mb-large">
          {cardCreditsSummary?.total_credit && formatMoney(cardCreditsSummary.total_credit)}{' '}
          {holderAccount?.currency ? holderAccount?.currency.name : ''}
        </div>

        {haveGradualDissolutionOfCredit() && (
          <>
            <div className="text-color-grey-600">
              <Trans>Reálny zostatok</Trans>:
            </div>

            <div className="mb-large">
              <strong>
                {cardCreditsSummary?.real_credit && formatMoney(cardCreditsSummary.real_credit)}{' '}
                {holderAccount.currency ? holderAccount.currency.name : ''}
              </strong>
            </div>
          </>
        )}

        <div className="text-color-grey-600">
          <Trans>Platnosť kreditu</Trans>:
        </div>
        {cardCreditsSummary?.credits && (
          <ul className="list--unstyled">
            {cardCreditsSummary.credits.map((expiration, index) => {
              return (
                <li key={index}>
                  <strong>
                    {formatMoney(expiration.amount!)}{' '}
                    {holderAccount?.currency ? holderAccount?.currency.name : ''}
                  </strong>{' '}
                  {expiration.unlimited_validity && <Trans>Neobmedzená platnosť kreditu</Trans>}
                  {!expiration.unlimited_validity && (
                    <span>
                      {expiration.expiry_date ? formatDate(expiration.expiry_date) : null}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {showAquaCreditsSummary && (
          <>
            <div className="text-color-grey-600 mt-large">
              <Trans>Dobitie nápojového kreditu za rok</Trans> {new Date().getFullYear()}:
            </div>
            <ul className="list--unstyled">
              <li>
                <strong>
                  {formatMoney(cardCreditsSummary?.credit_aqua_charge_annual_summary!)}{' '}
                  {holderAccount.currency ? holderAccount.currency.name : ''}
                </strong>
              </li>
            </ul>
          </>
        )}

        {showBenefit500CreditsSummary && (
          <>
            <div className="text-color-grey-600 mt-large">
              <Trans>Dobitie kreditu Benefit 500 za rok</Trans> {new Date().getFullYear()}:
            </div>
            <ul className="list--unstyled">
              <li>
                <strong>
                  {formatMoney(cardCreditsSummary?.credit_benefit500_charge_annual_summary!)}{' '}
                  {holderAccount.currency ? holderAccount.currency.name : ''}
                </strong>
              </li>
            </ul>
          </>
        )}
      </DialogContent>
    )
  }

  return (
    <ReachDialog onDismiss={close}>
      <DialogHeader title={_(t`Platnosť kreditu`)} />
      {modalContent()}
      <DialogFooter>
        <ButtonLayout direction="horizontal" className="align-items-right">
          <Button onClick={close}>
            <Trans>Zatvoriť</Trans>
          </Button>
        </ButtonLayout>
      </DialogFooter>
    </ReachDialog>
  )
})

CreditExpirationModal.displayName = 'CreditExpirationModal'
